/**
 * Copyright © 2025-2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { MsalProvider } from "@azure/msal-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import ReactDOM from "react-dom/client";

import msalInstance from "./features/azure-b2c/msalInstance";
import Analytics from "./services/analytics";
import { cachePurge } from "./util/helpers";
import App from "./App";
import { reportHandler } from "./reportHandler";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

import "./styles/tailwind.css";
import "./styles/root.scss";

dayjs.extend(relativeTime);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
Analytics.initialize();
cachePurge();

const supportsDvh = () =>
  typeof window.CSS !== "undefined" && window.CSS.supports?.("height", "100dvh");

const supportsZoom = () => typeof window.CSS !== "undefined" && window.CSS.supports?.("zoom", "1");

const documentHeight = () => {
  let scale = 1;

  // Check if the window height is 720 pixels or less. Browsers without CSS `zoom` stay at
  // scale 1, which falls through to the unscaled 100dvh/100vw layout below — the oversized
  // 133.33vw/vh box is only safe when `zoom` scales it back down to the viewport.
  if (window.innerHeight <= 720 && window.innerWidth > 1200 && supportsZoom()) {
    scale = 0.75; // 720p is 75% of 1080p
  }

  const rootEl = document.getElementById("root");
  const doc = document.documentElement;
  const adjustedHeight = window.innerHeight / scale;
  const useDvh = scale === 1 && supportsDvh();

  if (rootEl) {
    // `zoom` rather than `transform: scale()`: it affects layout, so the oversized #root
    // renders exactly one viewport tall and adds no document scroll range, and it does not
    // become a containing block for `position: fixed` descendants such as SidebarNav.
    // vw/vh are unaffected by `zoom`, so the 100 / scale sizing is what it scales back down.
    rootEl.style.zoom = `${scale}`;
    // `transform` made #root a stacking context as a side effect, which kept the header's
    // z-index: 1100 from outranking overlays portalled to document.body (Radix popovers ship
    // z-50). `zoom` does not create one, so isolate explicitly.
    rootEl.style.isolation = "isolate";
    rootEl.style.width = `${100 / scale}vw`;
    rootEl.style.height = useDvh ? "100dvh" : `${100 / scale}vh`;
  }

  doc.style.setProperty("--doc-height", useDvh ? "100dvh" : `${adjustedHeight}px`);
};

window.addEventListener("resize", documentHeight);
documentHeight();

root.render(
  <MsalProvider instance={msalInstance}>
    <App />
  </MsalProvider>
);

// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(log) from ./util/log)
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(reportHandler);
